'use client';

import { Color } from '@tiptap/extension-color';
import ListItem from '@tiptap/extension-list-item';
import TextStyle from '@tiptap/extension-text-style';
import { EditorProvider } from '@tiptap/react';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';

import { useNookSync } from '@/hooks/syncing/useNookSync';
import { EditorProps } from '@/types/editor';
import './styles.css';
import MenuBar from './MenuBar';
import MinFooter from '@/components/ui/Footer/MinFooter';

const extensions = [
  Color.configure({ types: [TextStyle.name, ListItem.name] }),
  TextStyle.configure(),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
    },
  }),
  Placeholder.configure({
    placeholder: 'Start writing here...',
  }),
];

const Editor = ({ initialContent, clientVersion, nookId }: EditorProps) => {
  return (
    <EditorProvider
      slotBefore={<MenuBar />}
      extensions={extensions}
      content={initialContent}
      immediatelyRender={false}
      editorContainerProps={{
        className:
          'h-[73dvh] card card-border shadow-md text-stone-950 p-4 overflow-y-auto dark:text-[var(--color-accent)]',
      }}
      editorProps={{
        attributes: {
          'aria-label': 'Rich Text Editor',
        },
      }}
    >
      <div className='mt-2 text-xs flex flex-row justify-between'>
        <SyncManager nookId={nookId} clientVersion={clientVersion} />
        <MinFooter />
      </div>
    </EditorProvider>
  );
};

const SyncManager = ({
  nookId,
  clientVersion,
}: {
  nookId: string;
  clientVersion: number;
}) => {
  const { isSaving, saveStatus } = useNookSync(nookId, clientVersion);

  return isSaving ? (
    <div>
      <span>Saving</span>
      <span className='loading loading-dots loading-xs ml-2'></span>
    </div>
  ) : (
    <span className={saveStatus.error ? 'text-red-500 font-semibold' : ''}>
      {saveStatus.message}
    </span>
  );
};

export default Editor;
